import React from "react";
import { useParams, Link } from "react-router-dom";
import {
  Zap, DollarSign, Wrench, ShieldCheck, Sparkles, PanelsTopLeft, Bird, Activity, Box, Plug,
  BatteryCharging, Gauge, Siren, Thermometer, Droplets, RefreshCw, PlugZap,
  Home as HomeIcon, Sun, DoorOpen, Trees, Wind, Waves,
  Check, ArrowRight, Phone, ArrowLeft,
  FileCheck, Layers,
  Award, Handshake, Clock,
} from "lucide-react";
import SEO, { SITE_URL } from "../components/SEO";
import LeadForm from "../components/LeadForm";
import { services, brand } from "../data/site";
import { serviceAreas } from "../data/serviceAreas";
import NotFound from "./NotFound";

const extraIcons = { Zap, DollarSign, Wrench, Sparkles, PanelsTopLeft, Bird, Activity, Box, Plug, BatteryCharging, Gauge, Siren, Thermometer, Droplets, RefreshCw, PlugZap, ShieldCheck, Clock, FileCheck, Handshake, Award, Layers };

const iconMap = { Home: HomeIcon, Sun, Sparkles, FileCheck, Layers, RefreshCw, Droplets, ShieldCheck, Award, Handshake, Clock };

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);
  if (!service) return <NotFound />;
  const Icon = iconMap[service.icon];
  const others = services.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div>
      <SEO
        title={service.seoTitle || `${service.name} in Phoenix, AZ | Pepla Roofing`}
        description={
          service.seoDescription ||
          `${service.short} Phoenix and Maricopa County. Free estimates: ${brand.phone}.`
        }
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              "@id": `${SITE_URL}/services/${service.slug}#service`,
              name: `${service.name} in Phoenix and Maricopa County`,
              description: service.intro,
              serviceType: service.name,
              provider: { "@id": `${SITE_URL}/#business` },
              areaServed: { "@type": "AdministrativeArea", name: "Maricopa County, Arizona" },
              url: `${SITE_URL}/services/${service.slug}`,
              hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: `${service.name} services`,
                itemListElement: (service.maintenance?.items || service.features.map((f) => ({ title: f }))).map((i) => ({
                  "@type": "Offer",
                  itemOffered: { "@type": "Service", name: i.title, description: i.desc },
                })),
              },
            },
            ...(service.faqs
              ? [{
                  "@type": "FAQPage",
                  mainEntity: service.faqs.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
                }]
              : []),
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
                { "@type": "ListItem", position: 2, name: "Services", item: `${SITE_URL}/services` },
                { "@type": "ListItem", position: 3, name: service.name, item: `${SITE_URL}/services/${service.slug}` },
              ],
            },
          ],
        }}
      />

      {/* Hero */}
      <section className="relative pt-40 pb-24 section-dark text-white overflow-hidden">
        <div className="absolute inset-0">
          <img fetchPriority="high" decoding="async" src={service.hero} alt={service.name} className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/70 to-ink" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <Link to="/services" className="inline-flex items-center gap-2 text-white/60 hover:text-brand text-sm mb-6 transition-colors">
            <ArrowLeft size={16} /> All Services
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <span className="w-14 h-14 rounded-xl bg-brand flex items-center justify-center"><Icon size={28} className="text-ink" /></span>
            <h1 className="font-heading text-4xl md:text-6xl uppercase tracking-tight">{service.name}</h1>
          </div>
          <div className="brand-line mb-5" />
          <p className="text-white/75 text-lg max-w-2xl">{service.intro}</p>
        </div>
      </section>

      {/* Body */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-10 xl:gap-12">
          <div className="lg:col-span-3">
            <span className="kicker">What We Offer</span>
            <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mt-3 mb-6">{service.name} Services</h2>
            <div className="grid sm:grid-cols-2 gap-3 mb-12">
              {service.features.map((f) => (
                <div key={f} className="flex items-center gap-3 bg-secondary rounded-lg px-4 py-3">
                  <span className="w-6 h-6 rounded-full bg-brand/15 flex items-center justify-center shrink-0">
                    <Check size={14} className="text-brand" />
                  </span>
                  <span className="text-sm font-medium">{f}</span>
                </div>
              ))}
            </div>

            <div className="grid sm:grid-cols-3 gap-6">
              {service.benefits.map((b) => (
                <div key={b.title} className="card-lift bg-card border border-ink/10 rounded-2xl p-6">
                  <h3 className="font-heading text-lg uppercase tracking-wide text-brand-dark mb-2">{b.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>

            {!service.noProjectImage && (
              <div className="mt-12 rounded-2xl overflow-hidden shadow-xl img-zoom">
                <img
                  loading="lazy"
                  decoding="async"
                  src={service.project || service.hero}
                  alt={`${service.name} project by Pepla Roofing`}
                  className="w-full h-72 md:h-96 object-cover"
                />
              </div>
            )}

            <div className="mt-12">
              <h2 className="font-heading text-2xl uppercase tracking-tight mb-2">{service.name} Across Maricopa County</h2>
              <div className="brand-line mb-5" />
              <div className="flex flex-wrap gap-2">
                {serviceAreas.map((a) => (
                  <Link key={a.slug} to={`/service-areas/${a.slug}`} className="text-xs border border-ink/15 rounded-full px-3 py-1 text-ink/70 hover:border-brand hover:text-brand transition-colors">
                    {a.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Sticky form.
              id="quote" is the target the in-page CTAs jump to — without it
              those buttons scroll nowhere. scroll-mt clears the fixed header. */}
          <aside id="quote" className="lg:col-span-2 scroll-mt-28">
            <div className="lg:sticky lg:top-28">
              <div className="section-dark text-white rounded-2xl p-6 md:p-8">
                <h3 className="font-heading text-2xl uppercase tracking-wide mb-2">Request a Quote</h3>
                <p className="text-white/60 text-sm mb-6">Free, no-obligation estimate for your {service.name.toLowerCase()} project.</p>
                <LeadForm dark defaultService={service.name} />
              </div>
              <a href={`tel:${brand.phoneRaw}`} className="flex items-center justify-center gap-2 mt-4 border border-ink/15 hover:border-brand rounded-md py-4 font-semibold text-sm transition-colors">
                <Phone size={18} className="text-brand" /> {brand.phone}
              </a>

            </div>
          </aside>
        </div>
      </section>

      {/* Maintenance services — only renders for a service with a `maintenance` block */}
      {service.maintenance && (
        <section className="py-24 section-dark text-white">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">{service.maintenance.title}</h2>
              <div className="brand-line mx-auto mb-5" />
              <p className="text-white/70 text-lg">{service.maintenance.intro}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
              {service.maintenance.benefits.map((b) => {
                const BIcon = extraIcons[b.icon] || Check;
                return (
                  <div key={b.title} className="bg-white/5 border border-white/10 rounded-2xl p-7">
                    <BIcon size={30} className="text-brand mb-4" />
                    <h3 className="font-heading text-xl uppercase tracking-wide mb-2">{b.title}</h3>
                    <p className="text-white/65 text-sm leading-relaxed">{b.desc}</p>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {service.maintenance.items.map((m) => {
                const MIcon = extraIcons[m.icon] || Check;
                return (
                  <div key={m.title} className="card-lift bg-white/5 border border-white/10 rounded-2xl p-6 flex gap-4">
                    <span className="w-11 h-11 rounded-xl bg-brand/15 flex items-center justify-center shrink-0">
                      <MIcon size={20} className="text-brand" />
                    </span>
                    <div>
                      <h3 className="font-heading text-lg uppercase tracking-wide mb-1.5">{m.title}</h3>
                      <p className="text-white/65 text-sm leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="text-center mt-12">
              <a href="#quote" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors">
                {service.maintenance.cta || "Schedule Maintenance"} <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Desert climate — only renders for a service with a `climate` block */}
      {service.climate && (
        <section className="py-24 bg-background">
          {/* With a photo this is a two-column block; without one the text runs
              full width and the stat becomes its own band, rather than leaving
              an empty column and a floating card. */}
          {/* Only one max-w-* class may be emitted: Tailwind orders them by
              size in the stylesheet, so naming both lets the larger one win
              and the prose runs the full 7xl width. */}
          <div
            className={`mx-auto px-5 lg:px-8 ${
              service.climate.image
                ? "max-w-7xl grid lg:grid-cols-2 gap-12 items-center"
                : "max-w-3xl"
            }`}
          >
            <div>
              <span className="kicker">{service.climate.eyebrow}</span>
              <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">{service.climate.title}</h2>
              <div className="brand-line mb-6" />
              {service.climate.paragraphs.map((t) => (
                <p key={t.slice(0, 30)} className="text-muted-foreground leading-relaxed mb-5">{t}</p>
              ))}
              <ul className="space-y-3 mt-8">
                {service.climate.bullets.map((b) => (
                  <li key={b.label} className="flex gap-3">
                    <span className="w-6 h-6 rounded-full bg-brand/15 flex items-center justify-center shrink-0 mt-0.5">
                      <Check size={14} className="text-brand" />
                    </span>
                    <span className="text-sm leading-relaxed">
                      <strong className="font-semibold">{b.label}:</strong> {b.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            {service.climate.image ? (
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-xl">
                  <img loading="lazy" decoding="async" src={service.climate.image} alt={service.climate?.imageAlt || `${service.name} in the Arizona desert climate`} className="w-full aspect-[4/3] object-cover" />
                </div>
                <div className="section-dark text-white rounded-2xl p-8 max-w-xs shadow-2xl lg:absolute lg:-bottom-10 lg:-left-10 mt-6 lg:mt-0">
                  <div className="font-heading text-5xl text-brand leading-none">{service.climate.stat.value}</div>
                  <div className="text-white/70 text-xs uppercase tracking-widest mt-3 leading-relaxed">{service.climate.stat.label}</div>
                </div>
              </div>
            ) : (
              <div className="section-dark text-white rounded-2xl p-8 sm:p-10 mt-12 flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8">
                <div className="font-heading text-6xl text-brand leading-none shrink-0">{service.climate.stat.value}</div>
                <div className="text-white/80 text-sm leading-relaxed">{service.climate.stat.label}</div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Material comparison.
          A plain table, not cards — the whole point is reading across a row to
          compare one material against another on the same attribute, and cards
          break that alignment. Each row carries a "best for" so the table
          answers the decision rather than just listing specs.

          Rendered as a real <table> with scope="col"/"row" so a screen reader
          announces which material and which column a cell belongs to. On a
          phone it scrolls sideways inside its own container, with the material
          name frozen as the row header. */}
      {service.compare && (
        <section className="py-24 bg-background">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="max-w-3xl mb-12">
              <span className="kicker">{service.compare.eyebrow}</span>
              <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">{service.compare.title}</h2>
              <div className="brand-line mb-6" />
              <p className="text-muted-foreground leading-relaxed text-lg">{service.compare.intro}</p>
            </div>

            {/* The table scrolls sideways on a phone, and a scrollable region
                has to be reachable by keyboard or someone who cannot use a
                pointer can never see the right-hand columns. tabIndex={0} plus
                a labelled role makes it focusable and announced (WCAG 2.1.1). */}
            <div
              className="overflow-x-auto -mx-5 px-5 lg:mx-0 lg:px-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand rounded-lg"
              tabIndex={0}
              role="region"
              aria-label={service.compare.title}
            >
              <table className="w-full min-w-[720px] border-collapse text-sm">
                <caption className="sr-only">{service.compare.title}</caption>
                <thead>
                  <tr className="border-b-2 border-ink/15">
                    {service.compare.columns.map((c, i) => (
                      <th
                        key={c}
                        scope="col"
                        className={`text-left font-heading uppercase tracking-wide text-base py-4 pr-6 ${i === 0 ? "w-[22%]" : ""}`}
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {service.compare.rows.map((r) => (
                    <tr key={r.name} className="border-b border-ink/10 align-top">
                      <th scope="row" className="text-left font-semibold py-5 pr-6 leading-snug">
                        {r.name}
                        {r.note && (
                          <span className="block font-normal text-muted-foreground text-xs mt-1 leading-relaxed">{r.note}</span>
                        )}
                      </th>
                      {r.cells.map((cell, i) => (
                        <td key={i} className="py-5 pr-6 text-muted-foreground leading-relaxed">{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {service.compare.footnote && (
              <p className="text-muted-foreground text-sm leading-relaxed mt-8 max-w-3xl">{service.compare.footnote}</p>
            )}
          </div>
        </section>
      )}

      {/* Related service cross-link */}
      {service.related && (() => {
        const rel = services.find((x) => x.slug === service.related.slug);
        if (!rel) return null;
        const RIcon = iconMap[rel.icon] || Check;
        return (
          <section className="py-16 bg-secondary border-y border-ink/5">
            <div className="max-w-5xl mx-auto px-5 lg:px-8">
              <div className="bg-card border border-ink/10 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-8">
                <span className="w-16 h-16 rounded-2xl bg-brand flex items-center justify-center shrink-0">
                  <RIcon size={30} className="text-ink" />
                </span>
                <div className="flex-1">
                  <span className="kicker">{service.related.eyebrow}</span>
                  <h2 className="font-heading text-2xl md:text-3xl uppercase tracking-tight mt-2 mb-2">{service.related.title}</h2>
                  <p className="text-muted-foreground leading-relaxed">{service.related.text}</p>
                </div>
                <Link
                  to={`/services/${rel.slug}`}
                  className="shrink-0 inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-7 py-4 rounded-md transition-colors"
                >
                  {service.related.cta} <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Process timeline */}
      {service.process && (
        <section className="py-24 section-dark text-white">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="max-w-2xl mb-14">
              <span className="kicker-dark">{service.process.eyebrow}</span>
              <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">{service.process.title}</h2>
              <div className="brand-line mb-5" />
              <p className="text-white/70 text-lg">{service.process.intro}</p>
            </div>
            <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {service.process.steps.map((st, i) => (
                <li key={st.title} className="bg-white/5 border border-white/10 rounded-2xl p-7 relative">
                  <span className="font-heading text-5xl text-brand/25 absolute top-5 right-6 leading-none">{i + 1}</span>
                  <span className="inline-block text-brand text-xs font-semibold uppercase tracking-[0.2em] mb-3">{st.step}</span>
                  <h3 className="font-heading text-xl uppercase tracking-wide mb-2">{st.title}</h3>
                  <p className="text-white/65 text-sm leading-relaxed">{st.desc}</p>
                </li>
              ))}
            </ol>
            {service.process.disclaimer && (
              <p className="text-white/60 text-xs mt-8 max-w-3xl leading-relaxed">{service.process.disclaimer}</p>
            )}
          </div>
        </section>
      )}

      {/* FAQs */}
      {service.faqs && (
        <section className="py-24 bg-secondary">
          <div className="max-w-3xl mx-auto px-5 lg:px-8">
            <div className="text-center mb-12">
              <span className="kicker">Answers</span>
              <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">{service.name} FAQs</h2>
              <div className="brand-line mx-auto" />
            </div>
            <div className="space-y-4">
              {service.faqs.map((f) => (
                <div key={f.q} className="bg-card border border-ink/10 rounded-xl p-6">
                  <h3 className="font-heading text-lg uppercase tracking-wide mb-2">{f.q}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{f.a}</p>
                  {f.link && (
                    <Link to={f.link.to} className="inline-flex items-center gap-1 text-brand-dark text-sm font-semibold mt-3 hover:underline">
                      {f.link.label} <ArrowRight size={14} />
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 section-dark text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <h2 className="font-heading text-3xl uppercase tracking-tight mb-8 text-center">Explore More Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {others.map((s) => {
              const OIcon = iconMap[s.icon];
              return (
                <Link key={s.slug} to={`/services/${s.slug}`} className="group card-lift bg-white/5 border border-white/10 rounded-2xl p-6 flex items-center gap-4">
                  <span className="w-12 h-12 rounded-xl bg-brand flex items-center justify-center shrink-0"><OIcon size={22} className="text-ink" /></span>
                  <div>
                    <h3 className="font-heading text-lg uppercase tracking-wide group-hover:text-brand transition-colors">{s.name}</h3>
                    <span className="text-brand text-xs inline-flex items-center gap-1">View <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" /></span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
